import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { HttpService, IdSequence, NotificationService } from '../../../tms-shared';
import { TmsWebBaseEditCreateService } from '../base';
import {
  CalculateDeliveryDateRequest,
  CalculateDeliveryDateResponse,
  ConfirmSalesOrder,
  DeliveryDate,
  OrderLineStatusCheck,
  PriceFromPriceListRequest,
  PriceFromPriceListResponse,
  ProductsForCreatePurchaseOrder,
  PurchaseOrderSalesAllocation,
  SalesOrderAllocations,
  SalesOrderFromPreviousIds,
  SalesOrderHeaderLine,
  SalesOrderLine,
  SalesOrderLineAllocationsSummaryTop,
  SalesOrderLineBillOfMaterials,
  SalesOrderLineCommissionDetails,
  SalesOrderLineDetails,
  SalesOrderLineForCompleting,
  SalesOrderLinePreviousOrders,
  SalesOrderLinePurchaseAllocationsSummaryTop,
  SalesOrderLineQuickCreate,
  SalesOrderLineSummaryTop,
  SalesOrderMultiProductCreate,
} from '../../models';

@Injectable({
  providedIn: 'root',
})
export class SalesOrderLineService extends TmsWebBaseEditCreateService<SalesOrderLine, SalesOrderLineDetails> {
  public refreshDetails = new Subject<void>();

  constructor(
    protected httpService: HttpService,
    protected notificationService: NotificationService,
  ) {
    super('SalesOrderLine', $localize`Sales Order Line`, httpService, notificationService);
  }

  getForSalesOrderHeader(id: string): Observable<SalesOrderHeaderLine[]> {
    return this.httpService.get<SalesOrderHeaderLine[]>(`${this.apiUrl}/SalesOrderHeader?id=${id}`);
  }

  getSalesOrderLineIds(salesOrderHeaderId: string): Observable<IdSequence[]> {
    return this.httpService.get<IdSequence[]>(
      `${this.apiUrl}/GetSalesOrderLineIds?salesOrderHeaderId=${salesOrderHeaderId}`,
    );
  }

  getForAutoCreatePO(id: string): Observable<ProductsForCreatePurchaseOrder[]> {
    return this.httpService.get<ProductsForCreatePurchaseOrder[]>(
      `${this.apiUrl}/SalesOrderLinesForAutoCreatePO?id=${id}`,
    );
  }

  getSalesOrderBillOfMaterials(salesOrderLineId: string): Observable<SalesOrderLineBillOfMaterials[]> {
    return this.httpService.get<SalesOrderLineBillOfMaterials[]>(
      `${this.apiUrl}/GetSalesOrderBillOfMaterials?salesOrderLineId=${salesOrderLineId}`,
    );
  }

  getOutstandingOrderLines(): Observable<SalesOrderLineForCompleting[]> {
    return this.httpService.get<SalesOrderLineForCompleting[]>(`${this.apiUrl}/SalesOrderLinesForCompleting`);
  }

  getForSalesInvoiceCommission(): Observable<SalesOrderLineCommissionDetails[]> {
    return this.httpService.get<SalesOrderLineCommissionDetails[]>(`${this.apiUrl}/GetForSalesInvoiceCommission`);
  }

  salesOrderAllocations(id: string): Observable<SalesOrderAllocations> {
    return this.httpService.get<SalesOrderAllocations>(`${this.apiUrl}/SalesOrderAllocation?id=${id}`);
  }

  purchaseOrderSalesAllocation(id: string): Observable<PurchaseOrderSalesAllocation[]> {
    return this.httpService.get<PurchaseOrderSalesAllocation[]>(`${this.apiUrl}/PurchaseOrderSalesAllocation?id=${id}`);
  }

  summaryTop(id: string): Observable<SalesOrderLineSummaryTop> {
    return this.httpService.get<SalesOrderLineSummaryTop>(`${this.apiUrl}/SalesOrderLineSummaryTop?id=${id}`);
  }

  allocationsSummaryTop(id: string): Observable<SalesOrderLineAllocationsSummaryTop> {
    return this.httpService.get<SalesOrderLineAllocationsSummaryTop>(`${this.apiUrl}/AllocationsSummaryTop?id=${id}`);
  }

  purchaseAllocationsSummaryTop(id: string): Observable<SalesOrderLinePurchaseAllocationsSummaryTop> {
    return this.httpService.get<SalesOrderLinePurchaseAllocationsSummaryTop>(
      `${this.apiUrl}/PurchaseAllocationsSummaryTop?id=${id}`,
    );
  }

  getPreviousSalesOrders(salesOrderHeaderId: string, search: string): Observable<SalesOrderLinePreviousOrders[]> {
    return this.httpService.get<SalesOrderLinePreviousOrders[]>(
      `${this.apiUrl}/PreviousSalesOrders?salesOrderHeaderId=${salesOrderHeaderId}&search=${search}`,
    );
  }

  getCheckStatusChangeAllowed(salesOrderLineId: string, newStatusId: string): Observable<OrderLineStatusCheck> {
    return this.httpService.get<OrderLineStatusCheck>(
      `${this.apiUrl}/CheckStatusChangeAllowed?salesOrderLineId=${salesOrderLineId ?? ''}&newStatusID=${newStatusId}`,
    );
  }

  getReservationExpiryDate(calculate: boolean): Observable<Date> {
    return this.httpService.get<Date>(`${this.apiUrl}/GetReservationExpiryDate?calculate=${calculate}`);
  }

  quickCreate(salesOrderLineQuickCreate: SalesOrderLineQuickCreate): Observable<SalesOrderLineQuickCreate> {
    return this.httpService.post<SalesOrderLineQuickCreate>(`${this.apiUrl}/QuickCreate`, salesOrderLineQuickCreate);
  }

  confirmSalesOrder(id: string, sequence: number): Observable<ConfirmSalesOrder> {
    return this.httpService.post<ConfirmSalesOrder>(`${this.apiUrl}/ConfirmSalesOrder`, { id, sequence });
  }

  deliveryDate(model: DeliveryDate): Observable<DeliveryDate> {
    return this.httpService.post<DeliveryDate>(`${this.apiUrl}/DeliveryDate`, model);
  }

  getPriceFromPriceList(model: PriceFromPriceListRequest): Observable<PriceFromPriceListResponse> {
    return this.httpService.post<PriceFromPriceListResponse>(`${this.apiUrl}/PriceFromPriceList`, model);
  }

  getCalculateDeliveryDate(model: CalculateDeliveryDateRequest): Observable<CalculateDeliveryDateResponse> {
    return this.httpService.post<CalculateDeliveryDateResponse>(`${this.apiUrl}/CalculateDeliveryDate`, model);
  }

  createFromPreviousOrders(salesOrderHeaderId: string, ids: SalesOrderFromPreviousIds[]): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/CreateFromPreviousOrders`, { salesOrderHeaderId, ids });
  }

  completeOrderLines(ids: string[]): Observable<any> {
    return this.httpService.post<any>(`${this.apiUrl}/CompleteOrderLines`, { ids });
  }

  amendOrder(model: SalesOrderHeaderLine): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/AmendOrder`, model);
  }

  updateCustomerOrder(salesOrderHeaderId: string, purchaseOrderLineId: string): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/UpdateCustomerOrder`, { salesOrderHeaderId, purchaseOrderLineId });
  }

  createMultiProductOrderLines(salesOrderHeaderId: string, salesOrderMultiProducts: SalesOrderMultiProductCreate[]) {
    return this.httpService.post(`${this.apiUrl}/CreateMultiProductOrderLines`, {
      salesOrderHeaderId,
      salesOrderMultiProducts,
    });
  }
}
